import { useState, useMemo, useEffect } from 'react'
import { Box, Text, Group, Badge } from '@mantine/core'
import { useAtomValue, useSetAtom } from 'jotai'
import { IconBook, IconUsers, IconChartBar, IconFileText } from '@tabler/icons-react'
import { activeChatCharacterAtom } from '../../atoms/chatAtoms'
import { activeStoryThemeAtom } from '../../atoms/themeAtoms'
import { GameTabBar } from './GameTabBar'
import { StatusPanel } from './StatusPanel'
import { CrewPanel } from './CrewPanel'
import { DialoguePanel } from './DialoguePanel'
import { ChatPanel } from './ChatPanel'
import { DocumentsPanel } from './DocumentsPanel'
import { ThreadTitleCard } from './ThreadTitleCard'
import { TextSizeControl } from './TextSizeControl'

export function GameScreen({
  story,
  sessionRole,
  isTeamPlay,
  teamName,
  playerRoles,
  serverSessionId,
  currentBeat,
  currentThread,
  allThreads,
  currentThreadIndex,
  progress,
  overallProgress,
  selectedChoice,
  inputValue,
  generatedContent,
  isLoading,
  beatsLoading,
  canGoNext,
  onNext,
  onChoiceSelect,
  onInputSubmit,
  onInputChange,
  canGoPrev,
  onPrev,
  showTitleCard,
  onTitleCardNext,
  isCurrentBeatLocked,
  beats,
  currentIndex,
  liveThreadIndex,
  liveBeatIndex,
  isPositionReachable,
  jumpToPosition,
  skipToCurrent,
}) {
  const [dialogueExpanded, setDialogueExpanded] = useState(false)
  const [activeTab, setActiveTab] = useState('story')
  const activeChatCharacterId = useAtomValue(activeChatCharacterAtom)

  // Apply the story's assigned theme (story.options.theme) while playing, and
  // clear it on exit so the lobby/profile fall back to the default theme. The
  // resolver in ThemedApp reads this atom and reskins live.
  const setActiveStoryTheme = useSetAtom(activeStoryThemeAtom)
  const storyThemeId = story?.options?.theme || null
  useEffect(() => {
    setActiveStoryTheme(storyThemeId)
    return () => setActiveStoryTheme(null)
  }, [storyThemeId, setActiveStoryTheme])

  // Determine the active character from the current beat
  const activeCharacter = currentBeat?.character || null

  // Collect characters assigned to the story
  const characters = story?.characters || []

  // Resolve the player's chosen role to its display name (falls back to the
  // key if the definition is missing). Null when the story has no roles or the
  // player hasn't picked one yet.
  const roleName = sessionRole
    ? ((playerRoles || []).find((r) => r.key === sessionRole)?.name || sessionRole)
    : null

  // Resolve the character being chatted with (if any)
  const chatCharacter = activeChatCharacterId
    ? characters.find((c) => c.id === activeChatCharacterId)
    : null

  // Build tabs dynamically — include DOCS tab only when story has player-visible documents
  const tabs = useMemo(() => {
    const base = [
      { key: 'story', label: 'STORY', icon: IconBook },
      { key: 'crew', label: 'CREW', icon: IconUsers },
    ]
    if (story?.player_documents_count > 0) {
      base.push({ key: 'docs', label: 'DOCS', icon: IconFileText })
    }
    base.push({ key: 'status', label: 'STATUS', icon: IconChartBar })
    return base
  }, [story?.player_documents_count])

  // Scene background: beat overrides thread overrides story
  const sceneBackgroundUrl =
    currentBeat?.background_url ||
    currentThread?.background_url ||
    story?.default_background_url ||
    null

  return (
    <Box style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Story name bar — the persistent control window while playing */}
      <Box
        px="lg"
        py={8}
        style={{
          borderBottom: '1px solid var(--bw-panel-border)',
          background: 'var(--bw-panel-bg)',
        }}
      >
        <Group justify="space-between" wrap="nowrap" gap="sm">
          <Text size="sm" fw={600} ff="var(--bw-heading-font)" style={{ color: 'var(--bw-accent)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {story?.name || 'Loading...'}
          </Text>
          <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
            {isTeamPlay ? (
              <Badge
                size="sm"
                variant="light"
                color="teal"
                ff="var(--bw-heading-font)"
                leftSection={<IconUsers size={12} />}
                title={`Playing as a team${teamName ? `: ${teamName}` : ''}`}
              >
                {teamName ? `Team · ${teamName}` : 'Team'}
              </Badge>
            ) : (
              <Badge
                size="sm"
                variant="outline"
                color="gray"
                ff="var(--bw-heading-font)"
                title="Solo play — just you"
              >
                Solo
              </Badge>
            )}
            {roleName && (
              <Badge
                size="sm"
                variant="outline"
                color="violet"
                ff="var(--bw-heading-font)"
                title={`Your role: ${roleName}`}
              >
                {roleName}
              </Badge>
            )}
            {/* Always-available text-size picker (also in the dialogue bar) */}
            <TextSizeControl />
          </Group>
        </Group>
      </Box>

      {/* Game grid */}
      <Box
        style={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateRows: dialogueExpanded ? '0fr 1fr' : '1fr 50%',
          gridTemplateColumns: '1fr',
          position: 'relative',
          gap: 0,
          overflow: 'hidden',
          transition: 'grid-template-rows 0.3s ease',
        }}
      >
        {/* Scene background — rows 1-2, image fills height and overflows width */}
        {sceneBackgroundUrl && (
          <Box
            style={{
              gridColumn: '1 / -1',
              gridRow: '1 / 2',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Sharp image layer — fills full height, overflows left/right */}
            <div style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url(${sceneBackgroundUrl})`,
              backgroundSize: 'auto 100%',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }} />
            {/* Side fades — image edges dissolve into background color */}
            <div style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to right, rgba(10, 22, 40, 1) 0%, rgba(10, 22, 40, 0) 15%, rgba(10, 22, 40, 0) 85%, rgba(10, 22, 40, 1) 100%)',
            }} />
            {/* Bottom gradient — fades into dialogue area */}
            <div style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to bottom, rgba(10, 22, 40, 0.2) 0%, rgba(10, 22, 40, 0.5) 60%, rgba(10, 22, 40, 0.95) 100%)',
            }} />
          </Box>
        )}

        {/* Row 1: Center area — avatar display */}
        <Box
          style={{
            gridColumn: '1 / -1',
            gridRow: '1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            minHeight: 0,
            transition: 'opacity 0.3s ease',
            opacity: dialogueExpanded ? 0 : 1,
            position: 'relative',
          }}
        >
          {activeCharacter?.avatar_url && (
            <Box
              style={{
                width: 180,
                height: 180,
                borderRadius: '50%',
                overflow: 'hidden',
                border: '3px solid rgba(0, 255, 255, 0.2)',
                boxShadow: '0 0 30px rgba(0, 255, 255, 0.15), inset 0 0 20px rgba(0, 255, 255, 0.05)',
              }}
            >
              <img
                src={activeCharacter.avatar_url}
                alt={activeCharacter.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </Box>
          )}
        </Box>

        {/* Row 2: Tabbed panel */}
        <Box style={{ gridColumn: '1 / -1', gridRow: '2', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <GameTabBar activeTab={activeTab} onTabChange={setActiveTab} tabs={tabs} />

          <Box style={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
            <Box style={{ height: '100%', display: activeTab === 'story' ? 'flex' : 'none', flexDirection: 'column' }}>
              {showTitleCard ? (
                <ThreadTitleCard
                  thread={currentThread}
                  threadIndex={currentThreadIndex}
                  threadCount={allThreads?.length || 1}
                  onNext={onTitleCardNext}
                />
              ) : (
              <DialoguePanel
                currentBeat={currentBeat}
                character={activeCharacter}
                characters={characters}
                selectedChoice={selectedChoice}
                inputValue={inputValue}
                generatedContent={generatedContent}
                isLoading={isLoading || beatsLoading}
                canGoNext={canGoNext}
                onNext={onNext}
                onChoiceSelect={onChoiceSelect}
                onInputSubmit={onInputSubmit}
                onInputChange={onInputChange}
                expanded={dialogueExpanded}
                onToggleExpand={() => setDialogueExpanded(prev => !prev)}
                canGoPrev={canGoPrev}
                onPrev={onPrev}
                locked={isCurrentBeatLocked}
                timeline={{
                  threads: allThreads,
                  beats,
                  currentThreadIndex,
                  currentIndex,
                  liveThreadIndex,
                  liveBeatIndex,
                  isReachable: isPositionReachable,
                  onJump: jumpToPosition,
                  onSkipToCurrent: skipToCurrent,
                }}
              />
              )}
            </Box>

            <Box style={{ height: '100%', display: activeTab === 'crew' ? 'flex' : 'none', flexDirection: 'column' }}>
              {chatCharacter ? (
                <ChatPanel
                  character={chatCharacter}
                  storyId={story?.id}
                  serverSessionId={serverSessionId}
                  expanded={dialogueExpanded}
                  onToggleExpand={() => setDialogueExpanded(prev => !prev)}
                />
              ) : (
                <CrewPanel
                  characters={characters}
                  activeCharacterId={activeCharacter?.id}
                  allThreads={allThreads}
                  currentThreadIndex={currentThreadIndex}
                />
              )}
            </Box>

            {story?.player_documents_count > 0 && (
              <Box style={{ height: '100%', display: activeTab === 'docs' ? 'flex' : 'none', flexDirection: 'column' }}>
                <DocumentsPanel
                  storyId={story?.id}
                  sessionRole={sessionRole}
                  rolesRequired={(playerRoles?.length || 0) > 0}
                  hasSession={!!serverSessionId}
                />
              </Box>
            )}

            <Box style={{ height: '100%', display: activeTab === 'status' ? 'flex' : 'none', flexDirection: 'column' }}>
              <StatusPanel overallProgress={overallProgress} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
